/**
 * Astor Onboarding Types
 *
 * Core type definitions and Zod validation schemas for the Astor AI assistant
 * onboarding system. Defines the structure for onboarding plans, nudges,
 * SSE actions, and related validation schemas.
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import { z } from 'zod';

import type { PersonaType } from '../persona/persona-guide.types';

// ============================================================================
// SSE EVENT TYPE
// ============================================================================

/**
 * SSE event types emitted by the Astor streaming endpoint.
 * Used to discriminate event payloads in the AstorWidget SSE listener.
 */
export type AstorSSEEventType = 'chunk' | 'content' | 'sources' | 'action' | 'approval_request' | 'action_result';

// ============================================================================
// PORTAL TYPE
// ============================================================================

/**
 * Portal types for Astor context.
 * Named AstorPortalType to avoid collision with NavPortalType.
 */
export type AstorPortalType = 'admin' | 'auth' | 'business' | 'consumer' | 'partner';

/**
 * Zod schema for AstorPortalType validation
 */
export const astorPortalTypeSchema = z.enum(['admin', 'auth', 'business', 'consumer', 'partner']);

// ============================================================================
// ONBOARDING PLAN
// ============================================================================

/**
 * How an onboarding plan was generated
 */
export type OnboardingPlanGeneratedBy = 'ai' | 'admin_template' | 'system';

/**
 * Milestone types
 */
export type MilestoneType = 'action' | 'exploration' | 'configuration';

/**
 * Milestone statuses
 */
export type MilestoneStatus = 'locked' | 'available' | 'in_progress' | 'completed' | 'skipped';

/**
 * An action that Astor can perform or guide the user through
 */
export type AstorAction = NavigateAction | ExecuteAction | HighlightAction | SpotlightAction | SuggestAction;

/**
 * Navigate the user to a specific route
 */
export interface NavigateAction {
  type: 'navigate';
  /** Target path to navigate to */
  path: string;
  /** Optional query parameters */
  params?: Record<string, string>;
  /** Optional spotlight tour to start after navigation */
  spotlight?: { tourId: string; steps: SpotlightStep[] };
  /** Optional page transition style */
  transition?: 'instant' | 'smooth';
}

/**
 * Execute a tool/action with optional approval
 */
export interface ExecuteAction {
  type: 'execute';
  /** Unique ID for this tool call */
  toolCallId: string;
  /** Name of the tool to execute */
  toolName: string;
  /** Parameters for the tool */
  parameters: Record<string, unknown>;
  /** Whether the user must approve before execution */
  requiresApproval: boolean;
  /** Risk level of this action */
  riskLevel: 'low' | 'medium' | 'high';
  /** Structured preview of what will happen */
  preview: {
    title: string;
    description: string;
    fields: Array<{ label: string; value: string }>;
    warnings?: string[];
  };
}

/**
 * Highlight a UI element with a message
 */
export interface HighlightAction {
  type: 'highlight';
  /** CSS selector for the element to highlight */
  selector: string;
  /** Message to show near the highlighted element */
  message: string;
  /** Duration in milliseconds (auto-dismiss) */
  duration?: number;
}

/**
 * Spotlight tour step definition
 */
export interface SpotlightStep {
  /** CSS selector for the element to spotlight */
  target: string;
  /** Title for this step */
  title: string;
  /** Content/body text for this step */
  content: string;
  /** Tooltip placement relative to the target element */
  placement: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  /** Route to navigate to before highlighting (for multi-page tours) */
  route?: string;
}

/**
 * Start a spotlight tour
 */
export interface SpotlightAction {
  type: 'spotlight';
  /** Tour identifier */
  tourId: string;
  /** Ordered steps in the tour */
  steps: SpotlightStep[];
}

/**
 * Suggest further actions to the user
 */
export interface SuggestAction {
  type: 'suggest';
  /** Suggested actions, each containing a nested AstorAction */
  suggestions: SuggestionItem[];
}

/**
 * A single suggestion in a SuggestAction
 */
export interface SuggestionItem {
  /** Display label */
  label: string;
  /** Description of what this suggestion does */
  description?: string;
  /** Icon identifier for rendering in AstorWidget */
  icon: string;
  /** The action to perform if accepted */
  action: AstorAction;
}

/**
 * A milestone-specific action reference (simpler than the full AstorAction union).
 * Used within OnboardingMilestone to define what Astor does for this milestone.
 */
export interface MilestoneAstorAction {
  type: 'guided_conversation' | 'navigate' | 'spotlight_tour';
  payload: string;
}

/**
 * A single milestone within an onboarding section
 */
export interface OnboardingMilestone {
  /** Unique milestone ID */
  id: string;
  /** Display title */
  title: string;
  /** Detailed description */
  description: string;
  /** Type of milestone */
  type: MilestoneType;
  /** Current status */
  status: MilestoneStatus;
  /** When the milestone was completed */
  completedAt?: string;
  /** Criteria for automatic completion detection */
  completionCriteria: {
    type: 'route_visit' | 'api_action' | 'manual' | 'data_exists';
    value: string;
  };
  /** Action Astor performs to help with this milestone */
  astorAction: MilestoneAstorAction;
  /** Estimated time in minutes */
  estimatedMinutes: number;
  /** Whether this milestone can be skipped */
  optional: boolean;
}

/**
 * A section grouping related milestones
 */
export interface OnboardingSection {
  /** Unique section ID */
  id: string;
  /** Display title */
  title: string;
  /** Section description */
  description: string;
  /** Icon identifier */
  icon: string;
  /** Milestones within this section */
  milestones: OnboardingMilestone[];
  /** Display order */
  order: number;
  /** Optional condition to unlock this section */
  unlockCondition?: string;
}

/**
 * A full onboarding plan for a user
 */
export interface OnboardingPlan {
  /** Unique plan ID */
  id: string;
  /** User this plan belongs to */
  userId: string;
  /** Business context */
  businessId: string;
  /** Which portal this plan is for */
  portal: AstorPortalType;
  /** User persona */
  persona: PersonaType;
  /** When the plan was generated */
  generatedAt: string;
  /** How the plan was generated */
  generatedBy: OnboardingPlanGeneratedBy;
  /** Ordered sections */
  sections: OnboardingSection[];
  /** Structured plan metadata */
  metadata?: {
    /** Estimated total time to complete the plan in minutes */
    estimatedMinutes?: number;
    /** Whether this plan was customized by an admin */
    customizedByAdmin?: boolean;
    /** Template ID if generated from a template */
    templateId?: string;
  };
  /**
   * Persisted user state: whether the user has dismissed the onboarding UI
   * (rail + nudges) for this plan. Mirrors the server-side `dismissed` column
   * returned by `astor.getOnboardingPlan` so the rail stays hidden across
   * page navigations instead of reappearing on every plan refetch.
   */
  dismissed?: boolean;
  /** Persisted per-user completed-milestone IDs (denormalised from sections). */
  completedMilestones?: string[];
  /**
   * Server-computed completion percentage (0..100, up to 2 decimals).
   * Calculated as `(completedMilestones.length / totalMilestones) * 100` in
   * `astor-onboarding.service.ts#calculateCompletionPct`. Client may recompute
   * from `sections[].milestones[]`; no scaling needed before rendering.
   */
  completionPct?: number;
}

// ============================================================================
// NUDGE SYSTEM
// ============================================================================

/**
 * Action types within a nudge
 */
export type NudgeActionType = 'open_chat' | 'navigate' | 'start_tour' | 'dismiss' | 'execute';

/**
 * An action the user can take from a nudge
 */
export interface NudgeAction {
  /** Action type */
  type: NudgeActionType;
  /** Display label */
  label: string;
  /** Navigation path (for 'navigate' type) */
  path?: string;
  /** Tour ID (for 'start_tour' type) */
  tourId?: string;
  /**
   * Chat message to prefill when opening Astor (for 'open_chat' type).
   * Alias: `prefill` (design doc naming) is equivalent to `chatMessage`.
   */
  chatMessage?: string;
  /** Alias for chatMessage — matches design doc naming convention */
  prefill?: string;
  /** Tool call parameters (for 'execute' type) */
  toolName?: string;
  /** Tool call parameters (for 'execute' type) */
  parameters?: Record<string, unknown>;
}

/**
 * Nudge categories for classification
 */
export type NudgeCategory =
  | 'empty_state'
  | 'first_visit'
  | 'idle'
  | 'error_recovery'
  | 'milestone'
  | 'return_visit'
  | 'feature_discovery';

/**
 * Typed label for nudge triggers — superset of NudgeTriggerType values.
 * Defined here (instead of importing from nudge-rules.ts) to avoid
 * circular dependency since nudge-rules.ts imports from this file.
 */
export type NudgeTriggerLabel =
  | 'empty_state'
  | 'first_visit'
  | 'idle'
  | 'repeated_action'
  | 'milestone'
  | 'error_recovery'
  | 'return_visit'
  | 'feature_unused'
  | 'feature_discovery'
  | 'llm_evaluated';

/**
 * Priority levels for nudges (typed union per rule 11a)
 */
export type NudgePriority = 'low' | 'medium' | 'high';

/**
 * A contextual nudge from Astor
 */
export interface AstorNudge {
  /** Unique nudge ID */
  id: string;
  /** Nudge category */
  category: NudgeCategory;
  /** What triggered this nudge (typed label matching NudgeTriggerType) */
  trigger: NudgeTriggerLabel;
  /** Display title */
  title: string;
  /** Nudge message body */
  message: string;
  /** Available actions */
  actions: NudgeAction[];
  /** Nudge priority level */
  priority: NudgePriority;
  /** Auto-dismiss after N seconds (0 = no auto-dismiss) */
  autoDismissSeconds?: number;
  /** Whether this nudge should only be shown once per user */
  showOnce: boolean;
}

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

/**
 * Schema for validating a full onboarding plan
 */
export const onboardingPlanSchema = z.object({
  id: z.string().min(1),
  userId: z.string().uuid(),
  businessId: z.string().uuid(),
  portal: astorPortalTypeSchema,
  persona: z.string().min(1),
  generatedAt: z.string().min(1),
  generatedBy: z.enum(['ai', 'admin_template', 'system']),
  sections: z.array(
    z.object({
      id: z.string().min(1),
      title: z.string().min(1),
      description: z.string(),
      icon: z.string(),
      milestones: z.array(
        z.object({
          id: z.string().min(1),
          title: z.string().min(1),
          description: z.string(),
          type: z.enum(['action', 'exploration', 'configuration']),
          status: z.enum(['locked', 'available', 'in_progress', 'completed', 'skipped']),
          completedAt: z.string().optional(),
          completionCriteria: z.object({
            type: z.enum(['route_visit', 'api_action', 'manual', 'data_exists']),
            value: z.string().min(1),
          }),
          astorAction: z.object({
            type: z.enum(['guided_conversation', 'navigate', 'spotlight_tour']),
            payload: z.string().min(1),
          }),
          estimatedMinutes: z.number().min(0),
          optional: z.boolean(),
        }),
      ),
      order: z.number().int().min(0),
      unlockCondition: z.string().optional(),
    }),
  ),
  metadata: z
    .object({
      estimatedMinutes: z.number().min(0).optional(),
      customizedByAdmin: z.boolean().optional(),
      templateId: z.string().optional(),
    })
    .optional(),
});

/**
 * Schema for completing a milestone
 */
export const completeMilestoneSchema = z.object({
  milestoneId: z.string().min(1),
});

/**
 * Schema for syncing activity events
 */
export const syncActivitySchema = z.object({
  portal: astorPortalTypeSchema,
  events: z.array(
    z.object({
      type: z.string().min(1),
      timestamp: z.string().min(1),
      route: z.string().min(1),
      metadata: z.record(z.string(), z.unknown()).optional(),
    }),
  ),
});

/**
 * Schema for page context (used by nudge engine)
 */
export const pageContextSchema = z.object({
  route: z.string().min(1),
  portal: astorPortalTypeSchema,
  isEmpty: z.boolean().optional(),
  isFirstVisit: z.boolean().optional(),
  idleSeconds: z.number().min(0).optional(),
  errorsThisSession: z.number().int().min(0).optional(),
});

// ============================================================================
// SCHEMA INFERRED TYPES
// ============================================================================

export type OnboardingPlanInput = z.infer<typeof onboardingPlanSchema>;
export type CompleteMilestoneInput = z.infer<typeof completeMilestoneSchema>;
export type SyncActivityInput = z.infer<typeof syncActivitySchema>;
export type PageContextInput = z.infer<typeof pageContextSchema>;
